import { useRef } from 'react'

interface ResizeHandleProps {
  /** 'x' = 左右にドラッグして幅を変える（縦の境界線）。'y' = 上下にドラッグして高さを変える（横の境界線）。 */
  axis: 'x' | 'y'
  /**
   * true の時、ドラッグ方向とサイズ変化の符号を反転する。
   * ハンドルの右/下に「動かす対象のペイン」がある場合（右ペイン・タイムライン）に使う。
   */
  invert?: boolean
  /** ドラッグ開始時に現在のサイズ(px)を読む */
  getSize: () => number
  /** ドラッグ中、新しいサイズ(px)を都度渡す。min/max のクランプは呼び出し側（store）に任せて良い。 */
  setSize: (px: number) => void
  /** ダブルクリックで既定値に戻す */
  onDoubleReset: () => void
  className?: string
  'data-testid'?: string
}

/**
 * CapCut 参照仕様 §5 のペイン境界リサイズハンドル。6px 幅（高さ）の透明領域・
 * ホバーで水色 1px 線・ドラッグ中は pointer capture でイベントを取り逃さない
 * （UI_MK2_PHASE1.md §3-1: 「ドラッグ中は iframe/video 上でもイベントを取り逃さない
 * よう pointer capture を使う」）。
 */
export function ResizeHandle({
  axis,
  invert = false,
  getSize,
  setSize,
  onDoubleReset,
  className = '',
  'data-testid': testId,
}: ResizeHandleProps) {
  const drag = useRef<{ startPos: number; startSize: number } | null>(null)

  function handlePointerDown(e: React.PointerEvent<HTMLDivElement>) {
    e.preventDefault()
    e.currentTarget.setPointerCapture(e.pointerId)
    drag.current = { startPos: axis === 'x' ? e.clientX : e.clientY, startSize: getSize() }
  }

  function handlePointerMove(e: React.PointerEvent<HTMLDivElement>) {
    const state = drag.current
    if (!state) return
    const pos = axis === 'x' ? e.clientX : e.clientY
    const rawDelta = pos - state.startPos
    const delta = invert ? -rawDelta : rawDelta
    setSize(state.startSize + delta)
  }

  function endDrag(e: React.PointerEvent<HTMLDivElement>) {
    drag.current = null
    try {
      e.currentTarget.releasePointerCapture(e.pointerId)
    } catch {
      /* already released — ignore */
    }
  }

  return (
    <div
      role="separator"
      aria-orientation={axis === 'x' ? 'vertical' : 'horizontal'}
      data-testid={testId}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onDoubleClick={onDoubleReset}
      title="ドラッグでリサイズ・ダブルクリックで既定に戻す"
      className={`group shrink-0 relative z-10 touch-none ${
        axis === 'x' ? 'w-[6px] cursor-col-resize' : 'h-[6px] cursor-row-resize'
      } ${className}`}
    >
      <div
        className={`absolute bg-accent-blue opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none ${
          axis === 'x' ? 'inset-y-0 left-1/2 -translate-x-1/2 w-px' : 'inset-x-0 top-1/2 -translate-y-1/2 h-px'
        }`}
      />
    </div>
  )
}
